import type {
  SetUserEmployeeCostInput,
  UserEmployeeCost,
} from '@clockwise/clients/user-employee-costs';
import { ApiError, isoDateString } from '@clockwise/shared';
import { DatePicker } from '@clockwise/ui';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { type UseFormReturn, useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { z } from 'zod';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { userEmployeeCostsApi } from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';
import { todayIso } from '@/lib/user-display';

// Sheet that captures "set this user's cost to X effective Y." The
// server clips any prior open period and inserts the new row; the
// caller sees a single returned row.
const FormSchema = z.object({
  costPerHour: z
    .string()
    .min(1, 'Required')
    .refine((v) => {
      const n = Number(v);
      return Number.isFinite(n) && n >= 0;
    }, 'Must be a non-negative number'),
  effectiveFrom: isoDateString('Required'),
});
type FormValues = z.infer<typeof FormSchema>;

const FIELD_NAMES = ['costPerHour', 'effectiveFrom'] as const;
type FormField = (typeof FIELD_NAMES)[number];
function isFormField(s: string): s is FormField {
  return (FIELD_NAMES as readonly string[]).includes(s);
}

interface Props {
  userId: string;
  /** Most recent open period — shown as context in the sheet header. */
  currentCost: UserEmployeeCost | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function UserCostChangeSheet({ userId, currentCost, open, onOpenChange }: Props) {
  const queryClient = useQueryClient();

  const form = useForm<FormValues>({
    resolver: zodResolver(FormSchema),
    defaultValues: { costPerHour: '', effectiveFrom: '' },
  });

  // Reset to a useful default whenever the sheet opens. Today's date as
  // a sensible starting point for "change cost going forward"; the
  // current cost prefills the input so a small bump is one keystroke.
  useEffect(() => {
    if (!open) return;
    form.reset({
      costPerHour: currentCost?.costPerHour ?? '',
      effectiveFrom: todayIso(),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, currentCost?.id]);

  const mutation = useMutation({
    mutationFn: (values: FormValues) => {
      const payload: SetUserEmployeeCostInput = {
        userId,
        effectiveFrom: values.effectiveFrom,
        costPerHour: Number(values.costPerHour),
      };
      return userEmployeeCostsApi.set(payload);
    },
    onSuccess: () => {
      toast.success('Employee cost updated');
      queryClient.invalidateQueries({ queryKey: queryKeys.userEmployeeCosts.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.audit.all });
      onOpenChange(false);
    },
    onError: (err) => {
      if (err instanceof ApiError && err.fields?.length) {
        for (const fe of err.fields) {
          if (isFormField(fe.path)) form.setError(fe.path, { message: fe.message });
        }
        toast.error('Please fix the highlighted fields');
        return;
      }
      toast.error(err instanceof Error ? err.message : 'Save failed');
    },
  });

  const onSubmit = form.handleSubmit((v) => mutation.mutate(v));
  const errors = form.formState.errors;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="flex flex-col gap-0 sm:max-w-md">
        <SheetHeader>
          <SheetTitle>Change employee cost</SheetTitle>
          <SheetDescription>
            {currentCost ? (
              <>
                Currently <span className="font-medium">${currentCost.costPerHour}/hr</span>{' '}
                effective {currentCost.effectiveFrom}. A new period replaces it from the date you
                choose; the prior row is closed out automatically.
              </>
            ) : (
              <>This is the first cost recorded for this user.</>
            )}
          </SheetDescription>
        </SheetHeader>

        <form onSubmit={onSubmit} className="flex min-h-0 flex-1 flex-col">
          <div className="flex-1 space-y-4 overflow-y-auto px-4">
            <FieldRow
              id="costPerHour"
              label="New cost (USD / hour)"
              type="number"
              step="0.01"
              register={form.register('costPerHour')}
              error={errors.costPerHour?.message}
            />
            <div className="space-y-1.5">
              <Label htmlFor="effectiveFrom">Effective from</Label>
              <DatePicker
                id="effectiveFrom"
                value={form.watch('effectiveFrom')}
                onChange={(v) =>
                  form.setValue('effectiveFrom', v, { shouldValidate: true, shouldDirty: true })
                }
                ariaInvalid={!!errors.effectiveFrom}
              />
              {errors.effectiveFrom ? (
                <p className="text-xs text-destructive">{errors.effectiveFrom.message}</p>
              ) : null}
            </div>
            <p className="text-xs text-muted-foreground">
              You may choose a past or future date — past dates record a retroactive correction;
              future dates schedule a promotion.
            </p>
          </div>

          <SheetFooter className="flex-row justify-end border-t">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={mutation.isPending}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={mutation.isPending}>
              {mutation.isPending ? 'Saving…' : 'Save cost'}
            </Button>
          </SheetFooter>
        </form>
      </SheetContent>
    </Sheet>
  );
}

interface FieldRowProps {
  id: FormField;
  label: string;
  type?: string;
  step?: string;
  register: ReturnType<UseFormReturn<FormValues>['register']>;
  error?: string;
}

function FieldRow({ id, label, type = 'text', step, register, error }: FieldRowProps) {
  return (
    <div className="space-y-1.5">
      <Label htmlFor={id}>{label}</Label>
      <Input id={id} type={type} step={step} aria-invalid={Boolean(error)} {...register} />
      {error ? <p className="text-xs text-destructive">{error}</p> : null}
    </div>
  );
}
